<template>
  <div class="modern-auth-wrapper">
    <img
      :src="watermarkLogo"
      class="page-background-watermark"
      alt=""
    >

    <div class="modern-login-card">
      <b-row class="h-100 m-0">
        <b-col
          md="6"
          class="p-4 p-md-5 left-panel d-flex flex-column justify-content-center"
        >
          <div class="mb-2 text-center">
            <h2 class="font-weight-bold">
              Acesso ao sistema
            </h2>
            <p class="text-muted">
              Portal Interno Honorarium
            </p>
            <div class="logo-container mt-3 mb-2 d-flex justify-content-center">
              <b-img
                :src="logo"
                fluid
                alt="Honorarium Logo"
                style="max-height: 80px;"
              />
            </div>
          </div>

          <transition
            name="fade-slide"
            mode="out-in"
          >
            <div
              v-if="!confirmTenant"
              key="etapa1"
              class="w-100"
            >
              <validation-observer ref="validacao">
                <b-form
                  class="auth-login-form"
                  @submit.prevent
                >
                  <b-form-group
                    label="Usuário / E-mail"
                    label-for="login-email"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Email"
                      rules="required|email"
                    >
                      <b-form-input
                        id="login-email"
                        v-model="form.login"
                        :state="errors.length > 0 ? false:null"
                        name="login-email"
                        placeholder="Ex: seu@email.com"
                        class="light-input"
                      />
                      <small class="text-danger">
                        {{ errors.length > 0 ? 'E-mail inválido' : null }}
                      </small>
                    </validation-provider>
                  </b-form-group>

                  <b-form-group>
                    <div class="d-flex justify-content-between">
                      <label for="login-password">Senha</label>
                    </div>
                    <div class="password-light-wrapper">
                      <senha-input
                        v-model="form.senha"
                        required
                      />
                    </div>
                    <small class="text-danger mt-1 d-block">
                      {{ loginError }}
                    </small>
                  </b-form-group>

                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <b-form-checkbox
                      id="remember-me"
                      v-model="status"
                      name="checkbox-1"
                    >
                      <span
                        class="text-muted"
                        style="font-size: 0.9rem;"
                      >
                        Manter-me logado
                      </span>
                    </b-form-checkbox>
                  </div>

                  <b-button
                    type="submit"
                    variant="primary"
                    block
                    class="modern-btn mb-3"
                    @click="autenticar"
                  >
                    ENTRAR
                  </b-button>

                  <div class="text-center">
                    <b-link
                      class="text-muted small-link"
                      @click="forgotPassword()"
                    >
                      Esqueceu sua senha?
                    </b-link>
                  </div>

                  <b-alert
                    v-if="esqueci"
                    show
                    class="mt-3"
                    :variant="esqueci.error ? 'danger' : 'success'"
                  >
                    {{ esqueci.message }}
                  </b-alert>

                  <div class="divider my-3">
                    <span class="text-muted">OU</span>
                  </div>

                  <b-button
                    variant="outline-dark"
                    block
                    class="google-btn d-flex align-items-center justify-content-center"
                    @click="iniciarLoginAutenticacao"
                  >
                    <img
                      src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                      alt="Google Logo"
                      class="google-logo mr-2"
                    >
                    <span>Entrar com Google</span>
                  </b-button>
                </b-form>
              </validation-observer>
            </div>

            <div
              v-else
              key="etapa2"
              class="w-100"
            >
              <h4 class="mb-2 text-dark text-center">
                Selecione o Ambiente
              </h4>
              <p class="text-muted mb-4 text-center">
                Qual conta deseja acessar?
              </p>

              <div class="tenant-selection-modern">
                <button
                  v-for="tenant in tenants"
                  :key="tenant.tenantId"
                  class="tenant-btn-light"
                  @click="tenantLogin(tenant)"
                >
                  {{ tenant.tenantName }}
                </button>
              </div>

              <b-button
                variant="outline-dark"
                block
                class="mt-3"
                @click="backToLogin"
              >
                Voltar
              </b-button>
            </div>
          </transition>
        </b-col>

        <b-col
          md="6"
          class="right-panel d-none d-md-flex flex-column align-items-center justify-content-center text-center p-5"
        >
          <div class="brand-content">
            <h2 class="text-white font-weight-bold mb-2">
              Olá, Bem Vindo ao Honorarium!
            </h2>
            <p class="text-white-50">
              Gestão inteligente para escritórios contábeis.
            </p>
          </div>
        </b-col>
      </b-row>
    </div>

    <div class="footer-policies mt-3">
      <a
        href="https://app.honorarium.com.br/politica-de-privacidade"
        target="_blank"
        class="text-white-50 mx-2"
      >
        Privacidade
      </a>
      <span class="text-white-50">•</span>
      <a
        href="https://app.honorarium.com.br/politica-de-uso"
        target="_blank"
        class="text-white-50 mx-2"
      >
        Termos de Uso
      </a>
    </div>
  </div>
</template>

<script>
/* eslint-disable global-require */
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, email } from '@validations'
import { initGoogleOAuth2Login } from '@/shared/mixins/login-google/login-google'
import useJwt from '@/auth/jwt/useJwt'
import { clearLoggedIn, getUserData, updateAbility } from '@/auth/utils'

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    SenhaInput: () => import('@/shared/components/senha-input/SenhaInput.vue'),
  },
  data() {
    return {
      status: false,
      form: {
        login: '',
        senha: '',
      },
      esqueci: null,
      loginError: '',
      confirmTenant: false,
      selectedTenant: 0,
      tenants: [],
      logo: require('@/assets/images/logo/honorariumlogin5.png'),
      watermarkLogo: require('@/assets/images/logo/fundo3.png'),
      required,
      email,
    }
  },
  created() {
    clearLoggedIn()
  },
  methods: {
    autenticar() {
      this.loginError = ''
      this.$refs.validacao.validate()
        .then(() => {
          this.form.login = this.form.login.trim()
          useJwt.loginStepOne(this.form)
            .then(response => {
              if (response.data.alterarSenha) {
                this.$router.push({ name: 'alterar-senha-interno' })
              } else {
                localStorage.setItem('userData', JSON.stringify(response.data))
                useJwt.setToken(response.data.tempToken)
                updateAbility(this.$ability)
                if (response.data.tenants.length > 1) {
                  this.tenants = response.data.tenants
                  this.confirmTenant = true
                } else if (response.data.tenants.length === 1) {
                  this.tenantLogin(response.data.tenants[0])
                } else {
                  this.$erro('Nenhum ambiente vinculado a sua conta. Contate o suporte')
                }
              }
            })
            .catch(error => {
              this.$erro('E-mail ou senha inválidos.')
              this.loginError = error.response && error.response.data ? error.response.data.detail : 'Erro de conexão'
              this.$refs.validacao.setErrors({ Password: this.loginError })
            })
        })
    },
    forgotPassword() {
      if (!this.form.login) {
        this.$erro('Digite seu e-mail primeiro')
        return
      }
      this.form.login = this.form.login.trim()
      useJwt.esqueciSenha(this.form.login)
        .then(() => {
          this.esqueci = {
            message: `E-mail enviado com sucesso para ${this.form.login}`,
            error: false,
          }
        })
        .catch(err => {
          const msg = (!err.message || !err.response || !err.response.data)
            ? 'Não foi possível enviar o email. Contate o administrador'
            : err.response.data.detail

          this.esqueci = {
            message: msg,
            error: true,
          }
        })
    },
    iniciarLoginAutenticacao() {
      useJwt.getClientGoogle()
        .then(payload => {
          initGoogleOAuth2Login(this.loginGoogle, payload.data)
        })
    },
    loginGoogle(tokenResponse) {
      useJwt.loginGoogle(tokenResponse.credential)
        .then(response => {
          if (response.data.alterarSenha) {
            this.$router.push({ name: 'alterar-senha-interno' })
          } else {
            localStorage.setItem('userData', JSON.stringify(response.data))
            useJwt.setToken(response.data.tempToken)
            updateAbility(this.$ability)
            if (response.data.tenants.length > 1) {
              this.tenants = response.data.tenants
              this.confirmTenant = true
            } else if (response.data.tenants.length === 1) {
              this.tenantLogin(response.data.tenants[0])
            } else {
              this.$erro('Nenhum ambiente vinculado a sua conta.')
            }
          }
        })
        .catch(err => {
          this.$erro(err.response ? err.response.data.detail : err.message)
          throw err
        })
    },
    backToLogin() {
      this.form.login = ''
      this.form.senha = ''
      this.confirmTenant = false
    },
    tenantLogin(tenant) {
      const userData = getUserData()
      const data = {
        tempToken: userData.tempToken,
        tenantIdentifier: tenant.tenantIdentifier,
        tenantId: tenant.tenantId,
      }
      const { tenantIdentifier } = tenant

      this.$isLoading(true)

      localStorage.setItem('tenantInfo', JSON.stringify({
        tenantId: tenant.tenantId,
        tenantIdentifier: tenant.tenantIdentifier,
        tenantName: tenant.tenantName,
      }))

      useJwt.loginStepTwo(tenantIdentifier, data)
        .then(response => {
          localStorage.setItem('userData', JSON.stringify(response.data.userData))

          const tenantInfo = response.data.tenantInfo || {
            tenantId: tenant.tenantId,
            tenantIdentifier: tenant.tenantIdentifier,
            tenantName: tenant.tenantName,
          }
          localStorage.setItem('tenantInfo', JSON.stringify(tenantInfo))

          useJwt.setToken(response.data.accessToken)
          updateAbility(this.$ability)

          localStorage.setItem('moduloSelecionado', 'Comercial')

          this.$router.push({ name: 'dashboard-comercial' })
        })
        .catch(error => {
          const resolved = this.$resolveError(error)
          this.loginError = resolved.detail || resolved.title || 'Erro ao acessar'
          this.$handleError(error)
        })
        .finally(() => {
          this.$isLoading(false)
        })
    },

  },
}
</script>

<style scoped>
.modern-auth-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #000000 0%, #1a1a1a 70%, #443910 100%);
  padding: 1rem;
  position: relative;
  overflow: hidden;
}

.page-background-watermark {
  position: absolute;
  top: 0;
  right: 0;
  left: auto;
  transform: scale(0.6  );
  transform-origin: top right;

  min-width: 100vw;
  opacity: 0.04;
  pointer-events: none;
  z-index: 0;
}

.modern-login-card {
  width: 100%;
  max-width: 900px;
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.9), 0 0 15px rgba(0,0,0,0.5);
  overflow: hidden;
  min-height: 500px;
  position: relative;
  z-index: 10;
}

.left-panel {
  background-color: #ffffff;
  color: #333333;
}

.right-panel {
  background-color: #1a1a1a;
  color: #ffffff;
  position: relative;
  overflow: hidden;
}

.brand-content {
  position: relative;
  z-index: 2;
}

.custom-label label {
  color: #b0b0b0;
  font-size: 0.85rem;
}

:deep(.form-control),
:deep(.input-group-text) {
  border-radius: 6px;
  height: 45px;
  border: 1px solid #ced4da;
  background-color: #fff;
  color: #495057;
}

:deep(.form-control:focus) {
  border-color: #d4af37 !important;
  box-shadow: 0 0 0 0.2rem rgba(212, 175, 55, 0.25);
}

.password-light-wrapper :deep(.input-group-text) {
  background-color: #fff;
  border-left: none;
}

.modern-btn {
  background-color: #d4af37;
  border: none;
  font-weight: 700;
  letter-spacing: 0.5px;
  border-radius: 20px;
  padding: 0.6rem;
  color: #000000;
  transition: all 0.3s;
}

.modern-btn:hover {
  background-color: #b5952f;
  color: #000;
  transform: translateY(-1px);
}

.google-btn {
  background-color: #ffffff;
  border: 1px solid #dadce0;
  color: #3c4043;
  font-weight: 500;
  border-radius: 20px;
  position: relative;
  transition: all 0.2s;
}

.google-btn:hover {
  background-color: #f7f8f8;
  border-color: #d2e3fc;
  box-shadow: 0 1px 2px rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15);
  color: #202124;
}

.google-logo {
  width: 18px;
  height: 18px;
}

.divider {
  display: flex;
  align-items: center;
  text-align: center;
}
.divider::before,
.divider::after {
  content: '';
  flex: 1;
  border-bottom: 1px solid #e0e0e0;
}
.divider span {
  padding: 0 10px;
  font-size: 0.8rem;
}

.small-link {
  font-size: 0.85rem;
  text-decoration: none;
}
.small-link:hover {
  color: #d4af37 !important;
  text-decoration: underline;
}

.tenant-selection-modern {
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: 300px;
  overflow-y: auto;
  padding-right: 5px;
}

.tenant-selection-modern::-webkit-scrollbar {
  width: 6px;
}
.tenant-selection-modern::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}
.tenant-selection-modern::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 3px;
}
.tenant-selection-modern::-webkit-scrollbar-thumb:hover {
  background: #999;
}

.tenant-btn-light {
  background-color: #f8f9fa;
  color: #333;
  border: 1px solid #ced4da;
  padding: 10px;
  border-radius: 6px;
  text-align: left;
  transition: 0.3s;
  flex-shrink: 0;
}

.tenant-btn-light:hover {
  background-color: #d4af37;
  border-color: #d4af37;
  color: #000;
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}
.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}
.fade-slide-enter-to,
.fade-slide-leave-from {
  opacity: 1;
  transform: translateX(0);
}

:deep(.custom-control-input:checked ~ .custom-control-label::before) {
  background-color: #d4af37;
  border-color: #d4af37;
}

/* Z-index para o rodapé */
.footer-policies {
  position: relative;
  z-index: 10;
}
</style>
